import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import ProfileCard from "@/components/ProfileCard";
import { Sparkles, Cross, BadgeCheck, HeartHandshake, ChevronRight, ArrowLeft } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/11813966/pexels-photo-11813966.jpeg";

export default function Welcome() {
    const [featured, setFeatured] = useState([]);
    useEffect(() => {
        client.get("/profiles/featured?limit=3").then(({ data }) => setFeatured(data)).catch(() => {});
    }, []);

    return (
        <div className="bg-white">
            {/* Banner */}
            <div className="bg-navy text-white py-3">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between gap-4 flex-wrap">
                    <div className="text-xs tracking-[0.18em] uppercase font-sans flex items-center gap-2">
                        <Cross className="h-3.5 w-3.5 text-gold" />
                        <span>Welcome, friends of Orthodox TV</span>
                    </div>
                    <a
                        href="https://www.orthodoxtv.in"
                        className="text-xs tracking-[0.18em] uppercase font-sans text-navy-200 hover:text-gold flex items-center gap-1"
                        data-testid="welcome-back-link"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to Orthodox TV
                    </a>
                </div>
            </div>

            {/* Hero */}
            <section className="py-16 md:py-24 linen-bg">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 bg-white border border-navy-100 rounded-lg px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase text-navy-600 font-semibold">
                            <Sparkles className="h-3.5 w-3.5 text-gold-600" />
                            <span>An Orthodox TV ministry</span>
                        </div>
                        <h1 className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl text-navy leading-[0.95] tracking-tighter">
                            Faithful readers,<br /><span className="italic text-gold-600">faithful partners</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-navy-500 leading-relaxed font-sans">
                            You've read our articles, watched our videos, studied with our community. Now find a
                            Christ-centered partner from the same Malankara Orthodox family.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/register"
                                data-testid="welcome-register-cta"
                                className="font-sans text-xs uppercase tracking-[0.18em] bg-gold text-navy rounded-lg px-8 py-4 hover:bg-gold-600 hover:text-white active:scale-95 transition-all shadow-lg shadow-gold/30"
                            >
                                Register Free
                            </Link>
                            <Link
                                to="/search"
                                data-testid="welcome-browse-cta"
                                className="font-sans text-xs uppercase tracking-[0.18em] border border-navy-300 text-navy rounded-lg px-8 py-4 hover:border-navy hover:bg-navy hover:text-white active:scale-95 transition-all flex items-center gap-2"
                            >
                                Browse Profiles <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <div className="mt-10 grid sm:grid-cols-3 gap-4">
                            <Perk icon={<BadgeCheck />} title="Verified" desc="Real, reviewed profiles" />
                            <Perk icon={<Cross />} title="Orthodox-first" desc="Faith at the center" />
                            <Perk icon={<HeartHandshake />} title="Serious" desc="Marriage-minded only" />
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-transparent to-navy/10 rounded-[240px_240px_24px_24px] blur-2xl"></div>
                        <div className="relative aspect-[4/5] overflow-hidden arch-mask-lg shadow-2xl">
                            <img src={HERO_IMG} alt="Orthodox couple" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured */}
            {featured.length > 0 && (
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">A glimpse</div>
                            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy tracking-tight">From our community</h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {featured.map((p) => <ProfileCard key={p.user_id} profile={p} />)}
                        </div>
                        <div className="text-center mt-10">
                            <Link to="/search" className="font-sans text-xs uppercase tracking-[0.18em] text-navy hover:text-gold-600 inline-flex items-center gap-1">
                                See all profiles <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* Why */}
            <section className="py-20 linen-bg">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight">Why HolyMarry?</h2>
                    <p className="mt-4 text-navy-500 font-sans max-w-2xl mx-auto">
                        Most matrimonial sites are generic, transactional, and lack reverence. HolyMarry is built specifically
                        for Orthodox Christians by people who share your faith.
                    </p>
                    <div className="mt-12 grid sm:grid-cols-3 gap-6 text-left">
                        <Block n="01" t="Denomination-aware" d="Filter by Malankara, Greek, Russian, Antiochian, Coptic, and more." />
                        <Block n="02" t="Reverent, not casual" d="No swipes. Express genuine, prayerful interest." />
                        <Block n="03" t="Trust by design" d="Verification badges, photo privacy, premium contact gating." />
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 bg-navy text-white text-center relative overflow-hidden">
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
                <div className="relative max-w-3xl mx-auto px-6">
                    <h2 className="font-serif text-5xl md:text-6xl tracking-tighter leading-tight">
                        Begin your<br /><span className="italic text-gold">sacred journey</span>
                    </h2>
                    <Link to="/register" className="inline-block mt-10 font-sans text-xs uppercase tracking-[0.18em] bg-gold text-navy rounded-lg px-10 py-4 hover:bg-white active:scale-95 transition-all shadow-2xl shadow-gold/30">
                        Register Free
                    </Link>
                </div>
            </section>
        </div>
    );
}

function Perk({ icon, title, desc }) {
    return (
        <div className="bg-white border border-navy-100 rounded-2xl p-4">
            <div className="h-9 w-9 rounded-full bg-linen text-gold-700 flex items-center justify-center mb-2">{icon}</div>
            <div className="font-sans font-semibold text-navy text-sm">{title}</div>
            <div className="text-xs text-navy-500 font-sans">{desc}</div>
        </div>
    );
}
function Block({ n, t, d }) {
    return (
        <div className="bg-white border border-navy-100 rounded-2xl p-6">
            <div className="font-serif text-3xl italic text-gold-600">{n}</div>
            <div className="mt-2 font-serif text-2xl text-navy">{t}</div>
            <p className="mt-1 text-sm text-navy-500 font-sans">{d}</p>
        </div>
    );
}
